import React, { useEffect, useState } from "react";
import axios from "axios";
import { Container, Row } from "shards-react";
import {
  Button,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Snackbar,
  TextField,
  Typography
} from "@material-ui/core";
import { createMuiTheme, makeStyles, MuiThemeProvider } from "@material-ui/core/styles";
import MuiAlert from "@material-ui/lab/Alert";
import Autocomplete from "@material-ui/lab/Autocomplete";
import AddIcon from "@material-ui/icons/Add";
import RemoveIcon from "@material-ui/icons/Remove";
import api from "../common/api";

function Alert(props) {
  return <MuiAlert elevation={6} variant="filled" {...props} />;
}

const DESTINATION_MODES = [
  { id: "type", name: "Tipo de subcontacto" },
  { id: "subcontact", name: "Subcontacto" },
  { id: "email", name: "Email" }
];

const DESTINATION_GROUPS = [
  { prefix: "to", label: "Para" },
  { prefix: "cc", label: "CC" },
  { prefix: "bcc", label: "CCO" }
];

const emptyDestination = prefix => ({
  [`${prefix}_mode`]: "",
  [`${prefix}_subcontact_type_id`]: "",
  [`${prefix}_subcontact_id`]: "",
  [`${prefix}_email`]: ""
});

const emptySubscription = (clientId, subscription = "") => ({
  id: null,
  client_id: clientId,
  subscription,
  subject: "",
  ...emptyDestination("to"),
  ...emptyDestination("cc"),
  ...emptyDestination("bcc")
});

const detectMode = (subcontactId, typeId, email) => {
  if (subcontactId) return "subcontact";
  if (typeId) return "type";
  if (email) return "email";
  return "";
};

const unwrapList = data => {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.data)) return data.data;
  return [];
};

const parseCatalogItem = item => {
  if (typeof item === "string") {
    return { subscription: item, subject: "" };
  }
  return {
    subscription: item.subscription || item.name || item.value || "",
    subject: item.subject || ""
  };
};

const normalizeId = value => (value === null || value === undefined ? "" : value);

const subcontactLabel = subcontact => {
  if (!subcontact) return "";
  const name = `${subcontact.firstname || ""} ${subcontact.lastname || ""}`.trim();
  const email =
    (subcontact.contact && subcontact.contact.email) || subcontact.email || "";
  if (name && email) return `${name} (${email})`;
  return name || email || `Subcontacto #${subcontact.id}`;
};

const mapApiSubscription = (item, clientId) => {
  const toSubcontactId =
    item.to_subcontact_id || (item.to_subcontact && item.to_subcontact.id) || "";
  const toTypeId =
    item.to_subcontact_type_id ||
    (item.to_subcontact_type && item.to_subcontact_type.id) ||
    "";
  const ccSubcontactId =
    item.cc_subcontact_id || (item.cc_subcontact && item.cc_subcontact.id) || "";
  const ccTypeId =
    item.cc_subcontact_type_id ||
    (item.cc_subcontact_type && item.cc_subcontact_type.id) ||
    "";
  const bccSubcontactId =
    item.bcc_subcontact_id ||
    (item.bcc_subcontact && item.bcc_subcontact.id) ||
    "";
  const bccTypeId =
    item.bcc_subcontact_type_id ||
    (item.bcc_subcontact_type && item.bcc_subcontact_type.id) ||
    "";

  return {
    id: item.id || null,
    client_id: item.client_id || clientId,
    subscription: item.subscription || "",
    subject: item.subject || "",
    to_mode: detectMode(toSubcontactId, toTypeId, item.to_email),
    to_subcontact_type_id: normalizeId(toTypeId),
    to_subcontact_id: normalizeId(toSubcontactId),
    to_email: item.to_email || "",
    cc_mode: detectMode(ccSubcontactId, ccTypeId, item.cc_email),
    cc_subcontact_type_id: normalizeId(ccTypeId),
    cc_subcontact_id: normalizeId(ccSubcontactId),
    cc_email: item.cc_email || "",
    bcc_mode: detectMode(bccSubcontactId, bccTypeId, item.bcc_email),
    bcc_subcontact_type_id: normalizeId(bccTypeId),
    bcc_subcontact_id: normalizeId(bccSubcontactId),
    bcc_email: item.bcc_email || ""
  };
};

const mergeSubscriptions = (catalog, existing, clientId) => {
  const byType = {};
  existing.forEach(item => {
    if (item.subscription) {
      byType[item.subscription] = item;
    }
  });

  const catalogTypes = catalog
    .map(parseCatalogItem)
    .filter(item => item.subscription);
  const existingTypes = existing
    .map(item => item.subscription)
    .filter(Boolean);
  const types = [];
  [...catalogTypes.map(item => item.subscription), ...existingTypes].forEach(
    type => {
      if (types.indexOf(type) === -1) types.push(type);
    }
  );

  if (types.length === 0) {
    return [emptySubscription(clientId)];
  }

  return types.map(type => {
    if (byType[type]) return byType[type];
    const catalogItem = catalogTypes.find(item => item.subscription === type);
    const row = emptySubscription(clientId, type);
    if (catalogItem && catalogItem.subject) {
      row.subject = catalogItem.subject;
    }
    return row;
  });
};

const useDestinationStyles = makeStyles({
  option: {
    fontSize: "0.75rem"
  },
  inputRoot: {
    fontSize: "0.75rem"
  }
});

const DestinationFields = ({
  prefix,
  label,
  subscription,
  onChange,
  subcontactTypes,
  subcontacts
}) => {
  const classes = useDestinationStyles();
  const mode = subscription[`${prefix}_mode`] || "";
  const selectedSubcontact =
    subcontacts.find(
      item =>
        String(item.id) === String(subscription[`${prefix}_subcontact_id`] || "")
    ) || null;

  return (
    <Row className="ml-1 mt-1 align-items-end">
      <TextField
        select
        id={`${prefix}_mode`}
        name={`${prefix}_mode`}
        margin="normal"
        className="mr-2 mt-1"
        value={mode}
        onChange={e => onChange(`${prefix}_mode`, e.target.value)}
        inputProps={{ style: { minWidth: 160, fontSize: "0.75rem" } }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Typography variant="caption" style={{ width: 40 }}>
                {label}
              </Typography>
            </InputAdornment>
          ),
          style: { fontSize: "0.75rem" }
        }}
      >
        <MenuItem value="" style={{ fontSize: "0.75rem" }}>
          Sin destinatario
        </MenuItem>
        {DESTINATION_MODES.map(option => (
          <MenuItem
            key={option.id}
            value={option.id}
            style={{ fontSize: "0.75rem" }}
          >
            {option.name}
          </MenuItem>
        ))}
      </TextField>

      {mode === "type" && (
        <TextField
          select
          id={`${prefix}_subcontact_type_id`}
          name={`${prefix}_subcontact_type_id`}
          margin="normal"
          className="mr-2 mt-1"
          value={subscription[`${prefix}_subcontact_type_id`] || ""}
          onChange={e =>
            onChange(`${prefix}_subcontact_type_id`, e.target.value)
          }
          inputProps={{ style: { minWidth: 180, fontSize: "0.75rem" } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Typography variant="caption" style={{ width: 90 }}>
                  Tipo
                </Typography>
              </InputAdornment>
            ),
            style: { fontSize: "0.75rem" }
          }}
        >
          <MenuItem value="" style={{ fontSize: "0.75rem" }}>
            Seleccione
          </MenuItem>
          {subcontactTypes.map(option => (
            <MenuItem
              key={option.id}
              value={option.id}
              style={{ fontSize: "0.75rem" }}
            >
              {option.name}
            </MenuItem>
          ))}
        </TextField>
      )}

      {mode === "subcontact" && (
        <Autocomplete
          options={subcontacts}
          getOptionLabel={subcontactLabel}
          getOptionSelected={(option, value) =>
            String(option.id) === String(value && value.id)
          }
          value={selectedSubcontact}
          onChange={(e, value) =>
            onChange(`${prefix}_subcontact_id`, value ? value.id : "")
          }
          classes={{ option: classes.option, inputRoot: classes.inputRoot }}
          ListboxProps={{ style: { fontSize: "0.75rem" } }}
          style={{ minWidth: 280 }}
          noOptionsText={
            <Typography variant="caption" style={{ fontSize: "0.75rem" }}>
              Sin resultados
            </Typography>
          }
          renderOption={option => (
            <Typography variant="caption" style={{ fontSize: "0.75rem" }}>
              {subcontactLabel(option)}
            </Typography>
          )}
          renderInput={params => (
            <TextField
              {...params}
              margin="normal"
              className="mr-2 mt-1"
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <React.Fragment>
                    <InputAdornment position="start">
                      <Typography variant="caption" style={{ width: 90 }}>
                        Subcontacto
                      </Typography>
                    </InputAdornment>
                    {params.InputProps.startAdornment}
                  </React.Fragment>
                ),
                style: { fontSize: "0.75rem" }
              }}
              inputProps={{
                ...params.inputProps,
                style: { fontSize: "0.75rem" }
              }}
            />
          )}
        />
      )}

      {mode === "email" && (
        <TextField
          id={`${prefix}_email`}
          name={`${prefix}_email`}
          type="email"
          margin="normal"
          className="mr-2 mt-1"
          value={subscription[`${prefix}_email`] || ""}
          onChange={e => onChange(`${prefix}_email`, e.target.value)}
          inputProps={{ style: { minWidth: 220, fontSize: "0.75rem" } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Typography variant="caption" style={{ width: 50 }}>
                  Email
                </Typography>
              </InputAdornment>
            ),
            style: { fontSize: "0.75rem" }
          }}
        />
      )}
    </Row>
  );
};

const getMuiTheme = () =>
  createMuiTheme({
    overrides: {
      MuiInput: {
        underline: {
          "&:before": {
            borderBottom: "0px"
          }
        }
      },
      MuiTypography: {
        caption: {
          fontWeight: 400
        }
      },
      MuiInputBase: {
        input: {
          borderBottom: "1px solid rgba(0, 0, 0, 0.42)",
          fontSize: "0.75rem"
        }
      },
      MuiAutocomplete: {
        option: {
          fontSize: "0.75rem"
        }
      }
    }
  });

const ClientSubscriptions = props => {
  const clientId = props.match.params.id;
  const token = localStorage.getItem("userToken");
  const authHeaders = { Authorization: `Bearer ${token}` };

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [client, setClient] = useState({});
  const [catalog, setCatalog] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [subcontactTypes, setSubcontactTypes] = useState([]);
  const [subcontacts, setSubcontacts] = useState([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success"
  });

  const fetchClient = async () => {
    const res = await axios.get(api.clients().get(clientId), {
      headers: authHeaders
    });
    return res.data[0] || {};
  };

  const fetchCatalog = async () => {
    const res = await axios.get(api.client_subscriptions().catalog(), {
      headers: authHeaders
    });
    return unwrapList(res.data)
      .map(parseCatalogItem)
      .filter(item => item.subscription);
  };

  const fetchSubscriptions = async () => {
    const res = await axios.get(api.client_subscriptions().get(clientId), {
      headers: authHeaders
    });
    return unwrapList(res.data).map(item =>
      mapApiSubscription(item, clientId)
    );
  };

  const fetchSubcontactTypes = async () => {
    const res = await axios.get(api.clients_subcontact_types, {
      headers: authHeaders
    });
    return unwrapList(res.data);
  };

  const fetchSubcontacts = async contactId => {
    if (!contactId) return [];
    const res = await axios.get(api.subcontacts().all(contactId), {
      headers: authHeaders
    });
    const data = res.data && res.data.data ? res.data.data : res.data;
    if (Array.isArray(data)) return data;
    if (data && typeof data === "object") return Object.values(data);
    return [];
  };

  const loadData = async () => {
    setLoading(true);
    // TODO: reemplazar por fetchCatalog() cuando el endpoint esté listo
    const hardcodedCatalog = [
      {
        subscription: "envio infracciones",
        subject: "Infracciones"
      }
    ];
    setCatalog(hardcodedCatalog);

    const safeFetch = async (fn, fallback) => {
      try {
        return await fn();
      } catch (error) {
        console.error(error);
        return fallback;
      }
    };

    try {
      const [clientData, subscriptionData, typesData] = await Promise.all([
        safeFetch(fetchClient, {}),
        safeFetch(fetchSubscriptions, []),
        safeFetch(fetchSubcontactTypes, [])
      ]);
      const clientSubcontacts = await safeFetch(
        () => fetchSubcontacts(clientData.contact_id),
        []
      );
      setClient(clientData);
      setSubcontactTypes(typesData);
      setSubcontacts(clientSubcontacts);
      setSubscriptions(
        mergeSubscriptions(hardcodedCatalog, subscriptionData, clientId)
      );
    } catch (error) {
      console.error(error);
      setSubscriptions(mergeSubscriptions(hardcodedCatalog, [], clientId));
      setSnackbar({
        open: true,
        message: "No se pudieron cargar las suscripciones",
        severity: "error"
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [clientId]);

  const usedTypes = subscriptions
    .map(item => item.subscription)
    .filter(Boolean);

  const availableTypes = catalog
    .map(item => item.subscription)
    .filter(type => usedTypes.indexOf(type) === -1);

  const updateSubscription = (index, field, value) => {
    setSubscriptions(prev => {
      const next = prev.slice();
      const row = { ...next[index], [field]: value };

      DESTINATION_GROUPS.forEach(({ prefix }) => {
        if (field === `${prefix}_mode`) {
          row[`${prefix}_subcontact_type_id`] = "";
          row[`${prefix}_subcontact_id`] = "";
          row[`${prefix}_email`] = "";
        }
      });

      next[index] = row;
      return next;
    });
  };

  const handleAdd = () => {
    const nextType = availableTypes[0] || "";
    const catalogItem = catalog.find(item => item.subscription === nextType);
    const row = emptySubscription(clientId, nextType);
    if (catalogItem && catalogItem.subject) {
      row.subject = catalogItem.subject;
    }
    setSubscriptions(prev => prev.concat(row));
  };

  const handleRemove = index => {
    setSubscriptions(prev => {
      if (prev.length === 1) {
        return [emptySubscription(clientId)];
      }
      return prev.filter((_, idx) => idx !== index);
    });
  };

  const toNull = value => {
    if (value === "" || value === null || value === undefined) return null;
    return value;
  };

  const toId = value => {
    const parsed = toNull(value);
    if (parsed === null) return null;
    const numeric = Number(parsed);
    return Number.isNaN(numeric) ? parsed : numeric;
  };

  const destinationId = (row, prefix, mode) =>
    row[`${prefix}_mode`] === mode
      ? toId(
          row[
            `${prefix}_${
              mode === "type" ? "subcontact_type_id" : "subcontact_id"
            }`
          ]
        )
      : null;

  const destinationEmail = (row, prefix) =>
    row[`${prefix}_mode`] === "email" ? toNull(row[`${prefix}_email`]) : null;

  const buildPayload = row => ({
    client_subscription: {
      client_id: Number(clientId),
      subscription: row.subscription,
      to_subcontact_type_id: destinationId(row, "to", "type"),
      to_subcontact_id: destinationId(row, "to", "subcontact"),
      cc_subcontact_type_id: destinationId(row, "cc", "type"),
      cc_subcontact_id: destinationId(row, "cc", "subcontact"),
      bcc_subcontact_type_id: destinationId(row, "bcc", "type"),
      bcc_subcontact_id: destinationId(row, "bcc", "subcontact"),
      to_email: destinationEmail(row, "to"),
      cc_email: destinationEmail(row, "cc"),
      bcc_email: destinationEmail(row, "bcc"),
      subject: toNull(row.subject)
    }
  });

  const hasDestination = row =>
    DESTINATION_GROUPS.some(({ prefix }) => {
      if (row[`${prefix}_mode`] === "type") {
        return Boolean(row[`${prefix}_subcontact_type_id`]);
      }
      if (row[`${prefix}_mode`] === "subcontact") {
        return Boolean(row[`${prefix}_subcontact_id`]);
      }
      if (row[`${prefix}_mode`] === "email") {
        return Boolean(row[`${prefix}_email`]);
      }
      return false;
    });

  const shouldSave = row =>
    Boolean(row.subscription) && (row.id || row.subject || hasDestination(row));

  const parseApiError = error => {
    const errors = error.response && error.response.data && error.response.data.errors;
    if (errors) {
      return Object.keys(errors)
        .map(key => errors[key])
        .join(" ");
    }
    if (error.response && error.response.data && error.response.data.message) {
      return error.response.data.message;
    }
    return "Ha ocurrido un error al guardar las suscripciones";
  };

  const handleSubmit = async event => {
    event.preventDefault();
    const rowsToSave = subscriptions.filter(shouldSave);
    if (rowsToSave.length === 0) {
      setSnackbar({
        open: true,
        message: "Complete al menos un destinatario o asunto",
        severity: "error"
      });
      return;
    }

    const missingType = rowsToSave.find(row => !row.subscription);
    if (missingType) {
      setSnackbar({
        open: true,
        message: "Seleccione el tipo de suscripción",
        severity: "error"
      });
      return;
    }

    const types = rowsToSave.map(row => row.subscription);
    const duplicated = types.find((type, idx) => types.indexOf(type) !== idx);
    if (duplicated) {
      setSnackbar({
        open: true,
        message: `Solo se permite una suscripción de tipo "${duplicated}"`,
        severity: "error"
      });
      return;
    }

    setSaving(true);
    try {
      const jsonHeaders = {
        "Content-Type": "application/json",
        ...authHeaders
      };
      for (const row of rowsToSave) {
        const payload = buildPayload(row);
        if (row.id) {
          await axios.post(
            api.client_subscriptions().patch(row.id),
            { _method: "PATCH", ...payload },
            { headers: jsonHeaders }
          );
        } else {
          await axios.post(api.client_subscriptions().post(), payload, {
            headers: jsonHeaders
          });
        }
      }
      setSnackbar({
        open: true,
        message: "Suscripciones guardadas con éxito",
        severity: "success"
      });
      await loadData();
    } catch (error) {
      setSnackbar({
        open: true,
        message: parseApiError(error),
        severity: "error"
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <MuiThemeProvider theme={getMuiTheme()}>
      <Container fluid className="main-content-container px-4 position-relative">
        <Snackbar
          open={snackbar.open}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          className="alert-fix"
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>

        {loading ? (
          <LinearProgress />
        ) : (
          <React.Fragment>
            <Row noGutters className="page-header">
              <Typography variant="h6">
                Suscripciones {client.business_name || ""}
              </Typography>
            </Row>

            {subscriptions.map((subscription, index) => {
              const otherUsedTypes = subscriptions
                .filter((_, idx) => idx !== index)
                .map(item => item.subscription)
                .filter(Boolean);
              const typeOptions = catalog
                .map(item => item.subscription)
                .filter(
                  type =>
                    type === subscription.subscription ||
                    otherUsedTypes.indexOf(type) === -1
                );
              if (
                subscription.subscription &&
                typeOptions.indexOf(subscription.subscription) === -1
              ) {
                typeOptions.unshift(subscription.subscription);
              }

              return (
                <div
                  key={subscription.id || `new-${index}`}
                  className="mb-3 p-3"
                  style={{
                    border: "1px solid rgba(0, 0, 0, 0.12)",
                    borderRadius: 4
                  }}
                >
                  <Row className="ml-1 align-items-end">
                    <TextField
                      select
                      id={`subscription.${index}`}
                      name={`subscription.${index}`}
                      margin="normal"
                      className="mr-2 mt-1"
                      disabled={Boolean(subscription.id)}
                      value={subscription.subscription || ""}
                      onChange={e =>
                        updateSubscription(index, "subscription", e.target.value)
                      }
                      inputProps={{
                        style: { minWidth: 180, fontSize: "0.75rem" }
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Typography variant="caption" style={{ width: 90 }}>
                              Tipo
                            </Typography>
                          </InputAdornment>
                        ),
                        style: { fontSize: "0.75rem" }
                      }}
                    >
                      <MenuItem value="" style={{ fontSize: "0.75rem" }}>
                        Seleccione
                      </MenuItem>
                      {typeOptions.map(option => (
                        <MenuItem
                          key={option}
                          value={option}
                          style={{ fontSize: "0.75rem" }}
                        >
                          {option}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      id={`subject.${index}`}
                      name={`subject.${index}`}
                      type="text"
                      margin="normal"
                      className="mr-2 mt-1"
                      value={subscription.subject || ""}
                      onChange={e =>
                        updateSubscription(index, "subject", e.target.value)
                      }
                      inputProps={{
                        style: { minWidth: 220, fontSize: "0.75rem" }
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Typography variant="caption" style={{ width: 60 }}>
                              Asunto
                            </Typography>
                          </InputAdornment>
                        ),
                        style: { fontSize: "0.75rem" }
                      }}
                    />
                    <IconButton
                      size="small"
                      onClick={handleAdd}
                      disabled={availableTypes.length === 0}
                    >
                      <AddIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleRemove(index)}
                      disabled={Boolean(subscription.id)}
                    >
                      <RemoveIcon />
                    </IconButton>
                  </Row>

                  {DESTINATION_GROUPS.map(group => (
                    <DestinationFields
                      key={`${index}-${group.prefix}`}
                      prefix={group.prefix}
                      label={group.label}
                      subscription={subscription}
                      subcontactTypes={subcontactTypes}
                      subcontacts={subcontacts}
                      onChange={(field, value) =>
                        updateSubscription(index, field, value)
                      }
                    />
                  ))}
                </div>
              );
            })}

            <Button
              className="mr-1 mt-3"
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={saving}
              style={{ fontSize: "0.7rem" }}
            >
              Guardar
            </Button>
            <Button
              variant="contained"
              className="mr-1 mt-3"
              color="secondary"
              href="/clients"
              style={{ fontSize: "0.7rem" }}
            >
              Cancelar
            </Button>
          </React.Fragment>
        )}
      </Container>
    </MuiThemeProvider>
  );
};

export default ClientSubscriptions;
